import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { IconButtonAnimate } from 'src/components/animate';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import Iconify from 'src/components/iconify';
import { MyInfoModal } from 'src/components/my-info';
import { useStores } from 'src/models';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

type Props = {
  isMini?: boolean;
};
function NavAccount({ isMini }: Props) {
  const { authStore } = useStores();
  const { user } = authStore;
  const { openDialog } = useDialogContext();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const currentLang = i18n.language;

  // 내 정보 수정 modal
  const handleOpenMyInfoModal = () => {
    openDialog({
      id: FORM_IDS.MYINFO,
      formType: FormType.UPDATE,
      dialogContent: <MyInfoModal />,
    });
  };

  // 언어 메뉴 열기
  const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // 언어 메뉴 닫기
  const handleCloseLangMenu = () => {
    setAnchorEl(null);
  };

  // 언어 변경
  const handleChangeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    handleCloseLangMenu();
  };

  const renderNavAccount = () => {
    return isMini ? (
      <></>
    ) : (
      <>
        <StyledRoot>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
              <OprtrInfoContainer>
                <OprtrInfoText>{user?.oprtrNm ?? '이름'}</OprtrInfoText>
                <OprtrInfoText variant="caption" color={'GrayText'} sx={{ whiteSpace: 'nowrap' }}>
                  {user?.loginId}
                </OprtrInfoText>
              </OprtrInfoContainer>
            </Stack>
            <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
              <IconBtn onClick={handleOpenLangMenu}>
                <Iconify icon="ph:translate" />
              </IconBtn>
              <IconBtn onClick={handleOpenMyInfoModal}>
                <Iconify icon="mdi:user" />
              </IconBtn>
              <IconBtn
                onClick={(e) => {
                  e.stopPropagation();
                  authStore.logout();
                }}
              >
                <Iconify icon="material-symbols:logout" />
              </IconBtn>
            </Stack>
          </Stack>
        </StyledRoot>

        {
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseLangMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => handleChangeLanguage('ko')} selected={currentLang === 'ko'}>
              KOR
            </MenuItem>
            <MenuItem onClick={() => handleChangeLanguage('en')} selected={currentLang === 'en'}>
              ENG
            </MenuItem>
          </Menu>
        }
      </>
    );
  };

  return renderNavAccount();
}

export default observer(NavAccount);

const StyledRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  textAlign: 'center',
  alignItems: 'center',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  margin: theme.spacing(2),
  backgroundColor: theme.palette.grey[200],
  padding: theme.spacing(2),
  boxShadow: theme.shadows[1],
}));

const OprtrInfoContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'flex-start',
  justifyContent: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '7px',
  margin: '5px 0',
}));

const OprtrInfoText = styled(Typography)(({ theme }) => ({
  lineHeight: 1,
}));

const IconBtn = styled(IconButtonAnimate)(({ theme }) => ({
  cursor: 'pointer',
  width: 30,
  height: 30,
  alignItems: 'center',
  padding: 0,
}));
